import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CachedCategoriesUtil } from '~app/modules/generic/utils/cached-categories.util';
import { APP_CATEGORY_ROUTE, FeatureToggleEnum, NUMBER_0 } from '~shared/constants';
import { Category } from '~app/modules/generic/models';
import { CategoriesService } from '~app/modules/generic/services/categories.service';
import { MediaObserver } from '@angular/flex-layout';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { CATEGORY_CAROUSEL_NEWSTYLE_OPTIONS, CATEGORY_CAROUSEL_OPTIONS } from './constants/category-carousel.constants';
import { CarouselComponent } from 'ngx-owl-carousel-o';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { PageDetectionUtil } from '~shared/utils/page-detection.util';
import { NavigationHelper } from '~shared/services/navigation-helper.service';
import { APP_BASE_HREF } from '@angular/common';

@Component({
  selector: 'app-category-carousel',
  templateUrl: './category-carousel.component.html',
  styleUrls: ['./category-carousel.component.scss'],
})
export class CategoryCarouselComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title?: string;
  @Input() limit?: number = 10;
  @Input() selectedCategories?: Category[];
  @Input() promotion?: boolean = true;
  @Input() isZeroResultsPage?: boolean;
  @Input() searchTerm?: string;

  @ViewChild('categoryCarousel') categoryCarousel: CarouselComponent;

  isMobile = false;
  categories: Category[];
  ngDestroy$ = new Subject();
  carouselOptions = CATEGORY_CAROUSEL_OPTIONS;
  newStyleCarousel: boolean;
  currentUrl = this.navigationHelper.getCurrentUrl();

  get isContentLandingPage(): boolean {
    return PageDetectionUtil.isLandingPagePage(this.currentUrl);
  }

  constructor(
    private categoriesService: CategoriesService,
    private mediaObserver: MediaObserver,
    private activeFeaturesConfigcatService: ActiveFeaturesConfigcatService,
    private navigationHelper: NavigationHelper,
    @Inject(APP_BASE_HREF) private baseHref: string
  ) {}

  ngOnInit(): void {
    this.initCategories();
    this.initializeMediaObservable();
    this.activeFeaturesConfigcatService
      .isFeatureEnabled(FeatureToggleEnum.PRODUCT_CARDS_CAROUSELS_RESTYLE)
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((value) => {
        this.newStyleCarousel = value;
        this.carouselOptions = this.newStyleCarousel ? CATEGORY_CAROUSEL_NEWSTYLE_OPTIONS : CATEGORY_CAROUSEL_OPTIONS;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCategories']) {
      this.initCategories();
    }
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  private getNavigationLink(path: string): string {
    const baseUrl = `/${this.baseHref.replace(new RegExp('/', 'g'), '')}`;
    return path.startsWith('/') ? `${baseUrl}${path}` : `${baseUrl}/${path}`;
  }

  navigateToCategory(category: Category): void {
    const url: string = this.getNavigationLink(`${APP_CATEGORY_ROUTE}/${category.slug}`);
    window.location.href = url;
  }

  initPromotionsCategory(): void {
    if (CachedCategoriesUtil.isCachedPromotionCategoriesExisted()) {
      this.categories = CachedCategoriesUtil.getCachedPromotionCategories();
    } else {
      this.categoriesService
        .getCategories()
        .pipe(takeUntil(this.ngDestroy$))
        .subscribe((items: Category[]) => {
          if (items.length > NUMBER_0) {
            this.categories = items || [];
          }
        });
    }
  }

  initCategories(): void {
    if (this.selectedCategories?.length > 0) {
      this.categories = this.selectedCategories;
    } else {
      this.initPromotionsCategory();
      this.initializeMediaObservable();
    }
  }

  initializeMediaObservable(): void {
    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(() => {
        this.isMobile = this.mediaObserver.isActive(MEDIA_ALISES.mobile);
      });
  }
}
